/**
 * Who may do what in the legal verification flow.
 *
 * These mirror `closedDealAttachmentAccess.ts` on the backend, which is what
 * actually enforces them — this is here so the UI does not offer a button that
 * comes back 403. When the two disagree, the backend is right.
 *
 * The split that matters: Legal records the verdict, Management releases the
 * deal. Sales does neither, because the person who uploaded a document is not
 * the person who accepts it.
 */

export const LEGAL_STATUS = {
  NOT_APPLICABLE: 'not_applicable',
  AWAITING_DOCUMENTS: 'awaiting_documents',
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  QUERY_RAISED: 'query_raised',
  LEGAL_VERIFIED: 'legal_verified',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  REOPENED: 'reopened'
};

const verticalOf = (session) => String(session?.vertical || '').toLowerCase();
const roleOf = (session) => String(session?.role || '').toLowerCase();

export function isAdminSession(session) {
  return roleOf(session) === 'admin';
}

export function isLegalVertical(session) {
  return verticalOf(session) === 'legal';
}

/** Read the KYC documents and the review card. */
export function canViewLegalReview(session) {
  if (!session) return false;
  if (isAdminSession(session)) return true;
  const vertical = verticalOf(session);
  return (
    vertical === 'legal' ||
    vertical === 'management' ||
    vertical === 'accounts' ||
    (vertical === 'sales' && session?.level === 'Medium')
  );
}

/** Hand a deal to Legal — whoever uploads its documents. */
export function canSubmitForLegalReview(session) {
  if (!session) return false;
  if (isAdminSession(session)) return true;
  const vertical = verticalOf(session);
  return (
    vertical === 'management' ||
    vertical === 'legal' ||
    (vertical === 'sales' && session?.level === 'Medium')
  );
}

/** Record the legal verdict: verify, query or reject. */
export function canRecordLegalVerdict(session) {
  if (!session) return false;
  if (isAdminSession(session)) return true;
  const vertical = verticalOf(session);
  return vertical === 'legal' || vertical === 'management';
}

/** Final approval that releases the deal. Management and admin only. */
export function canGiveFinalLegalApproval(session) {
  if (!session) return false;
  if (isAdminSession(session)) return true;
  return verticalOf(session) === 'management';
}

const STATUS_LABELS = {
  [LEGAL_STATUS.NOT_APPLICABLE]: 'Not started',
  [LEGAL_STATUS.AWAITING_DOCUMENTS]: 'Awaiting documents',
  [LEGAL_STATUS.SUBMITTED]: 'With Legal',
  [LEGAL_STATUS.IN_REVIEW]: 'In legal review',
  [LEGAL_STATUS.QUERY_RAISED]: 'Back with Sales',
  [LEGAL_STATUS.LEGAL_VERIFIED]: 'Legally verified',
  [LEGAL_STATUS.APPROVED]: 'Approved by Management',
  [LEGAL_STATUS.REJECTED]: 'Rejected',
  [LEGAL_STATUS.REOPENED]: 'Reopened'
};

export function legalStatusLabel(status) {
  return STATUS_LABELS[String(status || '').toLowerCase()] || 'Not started';
}

export function legalStatusClasses(status) {
  switch (String(status || '').toLowerCase()) {
    case LEGAL_STATUS.APPROVED:
      return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200';
    case LEGAL_STATUS.LEGAL_VERIFIED:
      return 'bg-teal-50 text-teal-800 ring-1 ring-teal-200';
    case LEGAL_STATUS.SUBMITTED:
    case LEGAL_STATUS.IN_REVIEW:
      return 'bg-sky-50 text-sky-800 ring-1 ring-sky-200';
    case LEGAL_STATUS.QUERY_RAISED:
    case LEGAL_STATUS.REOPENED:
      return 'bg-amber-50 text-amber-900 ring-1 ring-amber-200';
    case LEGAL_STATUS.REJECTED:
      return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200';
    default:
      return 'bg-slate-50 text-slate-700 ring-1 ring-slate-200';
  }
}

/**
 * Statuses that still represent open work for Legal.
 *
 * Legally verified is not one of them. Legal's stamp finishes the job; a
 * cleared deal sitting on the queue as "awaiting approval" read as an
 * outstanding task when there was nothing left to do.
 */
export function isLegalReviewOpen(status) {
  const s = String(status || '').toLowerCase();
  return (
    s === LEGAL_STATUS.SUBMITTED ||
    s === LEGAL_STATUS.IN_REVIEW ||
    s === LEGAL_STATUS.QUERY_RAISED ||
    s === LEGAL_STATUS.REOPENED
  );
}

/**
 * The deal has cleared Legal, and priorities, invoices and agreements follow.
 *
 * This is the question to ask — not whether Management approved, which is an
 * optional countersignature that most deals will never get.
 */
export function isLegallyCleared(status) {
  const s = String(status || '').toLowerCase();
  return s === LEGAL_STATUS.LEGAL_VERIFIED || s === LEGAL_STATUS.APPROVED;
}

/**
 * The stages a closed deal moves through, in order.
 *
 * Kept here rather than drawn inline so the queue and the deal panel cannot
 * disagree about what the flow is.
 */
export const LEGAL_FLOW_STAGES = [
  { id: 'closed', label: 'Deal closed' },
  { id: 'application', label: 'Application form' },
  { id: 'review', label: 'Legal verification' },
  { id: 'cleared', label: 'Legally verified' },
  { id: 'downstream', label: 'Priorities & invoices' }
];

/** How far along the flow a given status is, as an index into the stages. */
export function legalStageIndex(status) {
  const s = String(status || '').toLowerCase();
  if (isLegallyCleared(s)) return 4;
  if (
    s === LEGAL_STATUS.SUBMITTED ||
    s === LEGAL_STATUS.IN_REVIEW ||
    s === LEGAL_STATUS.QUERY_RAISED ||
    s === LEGAL_STATUS.REOPENED
  ) {
    return 2;
  }
  if (s === LEGAL_STATUS.AWAITING_DOCUMENTS) return 1;
  return 0;
}

/* ------------------------------------------------------- field comparisons */

export const MATCH_BAND = {
  PASS: 'pass',
  REVIEW: 'review',
  FAIL: 'fail',
  UNREADABLE: 'unreadable',
  NO_REFERENCE: 'no_reference'
};

const BAND_LABELS = {
  pass: 'Match',
  review: 'Needs a look',
  fail: 'Mismatch',
  unreadable: 'Could not read',
  no_reference: 'Nothing to compare'
};

export function bandLabel(band) {
  return BAND_LABELS[String(band || '').toLowerCase()] || 'Needs a look';
}

export function bandClasses(band) {
  switch (String(band || '').toLowerCase()) {
    case MATCH_BAND.PASS:
      return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200';
    case MATCH_BAND.FAIL:
      return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200';
    case MATCH_BAND.UNREADABLE:
    case MATCH_BAND.NO_REFERENCE:
      return 'bg-slate-100 text-slate-700 ring-1 ring-slate-300';
    default:
      return 'bg-amber-50 text-amber-900 ring-1 ring-amber-200';
  }
}

/**
 * What the reviewer is being asked to do about this field.
 *
 * The two that used to be one status: a document nobody could read needs a
 * pair of eyes on the scan, and a missing CRM value needs a keystroke. Saying
 * which is which is the difference between a queue that moves and one that
 * bounces deals back to Sales for no reason.
 */
export function bandGuidance(band) {
  switch (String(band || '').toLowerCase()) {
    case MATCH_BAND.PASS:
      return 'Matches the applicant record.';
    case MATCH_BAND.FAIL:
      return 'Does not match. Accept with a reason only if you can account for it.';
    case MATCH_BAND.UNREADABLE:
      return 'Nothing could be read from the document — read it yourself and type it in.';
    case MATCH_BAND.NO_REFERENCE:
      return 'Nothing was typed into the applicant record to compare against.';
    default:
      return 'Close, but not certain enough to pass on its own.';
  }
}

export const FIELD_LABELS = {
  pan: 'PAN number',
  aadhaar: 'Aadhaar number',
  voter_id: 'Voter ID number',
  name: 'Name',
  father_name: "Father's / husband's name",
  dob: 'Date of birth',
  address: 'Address',
  gender: 'Gender'
};

export function fieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

/** A field is settled when it passed cleanly or somebody decided it. */
export function isFieldSettled(row) {
  if (!row) return false;
  if (row.band === MATCH_BAND.PASS) return true;
  return row.verdict && row.verdict !== 'pending';
}

/**
 * Whether a reason is required to record this verdict on this field.
 *
 * Mirrors noteRequiredForFieldVerdict in legalTransitions.ts. Confirming a near
 * miss is ordinary work and needs no note; accepting a field nobody could read,
 * or one with nothing to compare against, is a judgement that goes on the
 * record. Enforced here as well as in the API so the reviewer finds out before
 * the round trip rather than after it.
 */
export function noteRequiredForFieldVerdict(band, verdict) {
  if (verdict === 'rejected') return true;
  if (verdict === 'accepted_with_exception') return true;
  return band !== MATCH_BAND.REVIEW;
}

/** The verdict an Accept means for this band. */
export function acceptVerdictFor(band) {
  return band === MATCH_BAND.FAIL ? 'accepted_with_exception' : 'accepted';
}
